import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ThunkGetCallScribeCall } from 'store/callScribe/thunks';
import {
  CallScribeIntelligenceEventCountForCallSelector,
  CallScribePipelineForCallSelector,
} from 'store/callScribe/selectors';
import {
  CallScribePipelineStage,
  ICallScribeCallListItem,
} from 'services/callScribe/types';
import { entryStillOwed } from 'components/CallScribe/postCallReviewPhase';

/**
 * How long to keep waiting before saying we cannot tell — twelve attempts at five seconds, so 60
 * seconds.
 *
 * Derived, not chosen: intelligence arrives in 6.1-12.8s across the organic population (n=13, median
 * 7.1s), measured as transcript-completion latency against the end of the call. 60s is a 4-5x margin
 * over the observed 13-second ceiling. Latency is roughly flat against call length (an 8s call took
 * 6.9s, a 94s call 10.1s), so this does not need to scale with duration.
 *
 * Two things the next person to touch this should know. The figure is a PROXY — transcript completion,
 * not end to end — so it understates by the summarisation step, which nobody has measured yet; if the
 * real CompletedAtUtc moves the organic band materially, 60s is still safe but this reasoning needs
 * redoing. And the aggregate of that same sample is worthless: p90 was 7,290s and max 982,800s, because
 * four re-processed rows sit between 49 minutes and 11 days with NOTHING between 13 seconds and 49
 * minutes. Reading the p90 would have produced a 7,300-second bound that looked measured and was worse
 * than the 400 seconds this replaces.
 *
 * That 400 seconds was a magic number, and at that length a genuine pipeline failure presented as
 * still-working for six and a half minutes, on a pipeline that finishes its real work in seven.
 */
const MAX_ATTEMPTS = 12;
const RECONCILE_MS = 5000;
const LOAD_GIVE_UP_MS = 20000;

const owesAutomaticPost = (row: ICallScribeCallListItem | undefined): boolean =>
  row?.willPostAutomatically === true &&
  row.hasIntelligence &&
  entryStillOwed(row.linkStatus);

const settled = (row: ICallScribeCallListItem): boolean =>
  row.pipelineStage === CallScribePipelineStage.Failed ||
  (row.hasIntelligence && !owesAutomaticPost(row));

export interface IUsePostCallIntelligenceResult {
  reviewCall: ICallScribeCallListItem | undefined;
  pipelineStalled: boolean;
  loadFailed: boolean;
  pipelineStage: CallScribePipelineStage | undefined;
  pipelineError: string | null;
  /**
   * Re-reads the call and RESOLVES WITH THE ROW, so a caller can decide what to render from the
   * refreshed state rather than from whatever it inferred locally. Callers that only want the
   * side effect can ignore the result.
   */
  refresh: () => Promise<ICallScribeCallListItem | undefined>;
}

export const usePostCallIntelligence = (
  callId: number | undefined,
  active: boolean,
): IUsePostCallIntelligenceResult => {
  const dispatch = useAppDispatch();
  const [reviewCall, setReviewCall] = useState<ICallScribeCallListItem | undefined>(undefined);
  const [pipelineStalled, setPipelineStalled] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadGeneration, setLoadGeneration] = useState(0);
  const readRef = useRef(false);

  useEffect(() => {
    readRef.current = false;
    setLoadFailed(false);
    setPipelineStalled(false);
  }, [callId]);

  useEffect(() => {
    if (!active || typeof callId !== 'number') return undefined;
    const timer = window.setTimeout(() => {
      if (!readRef.current) setLoadFailed(true);
    }, LOAD_GIVE_UP_MS);
    return () => { window.clearTimeout(timer); };
  }, [active, callId, loadGeneration]);

  const pipelineEvent = useAppSelector(CallScribePipelineForCallSelector(callId));
  const intelligenceEventCount = useAppSelector(
    CallScribeIntelligenceEventCountForCallSelector(callId),
  );

  const pipelineStage = pipelineEvent?.stage ?? reviewCall?.pipelineStage ?? undefined;
  const pipelineError = pipelineEvent?.error ?? reviewCall?.pipelineError ?? null;
  const failed = pipelineStage === CallScribePipelineStage.Failed;
  const awaitingAutomaticPost = owesAutomaticPost(reviewCall);

  const refresh = useCallback(async (): Promise<ICallScribeCallListItem | undefined> => {
    if (typeof callId !== 'number') return undefined;
    setLoadFailed(false);
    setLoadGeneration(n => n + 1);
    const action = await dispatch(ThunkGetCallScribeCall(callId));
    const row = (action.payload ?? undefined) as ICallScribeCallListItem | undefined;
    if (row) {
      readRef.current = true;
      setReviewCall(row);
      if (settled(row)) setPipelineStalled(false);
    }
    return row;
  }, [callId, dispatch]);

  useEffect(() => {
    if (!active || typeof callId !== 'number') return;
    refresh();
  }, [active, callId, refresh]);

  useEffect(() => {
    if (!active || intelligenceEventCount === 0) return;
    refresh();
  }, [active, intelligenceEventCount, refresh]);

  useEffect(() => {
    if (!active || typeof callId !== 'number') return undefined;
    if (failed || (reviewCall?.hasIntelligence && !awaitingAutomaticPost)) return undefined;

    let cancelled = false;
    let attempts = 0;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      attempts += 1;
      const action = await dispatch(ThunkGetCallScribeCall(callId)).catch(() => undefined);
      if (cancelled) return;
      const row = (action?.payload ?? undefined) as ICallScribeCallListItem | undefined;
      if (row) {
        readRef.current = true;
        setLoadFailed(false);
        setReviewCall(row);
        if (settled(row)) {
          return;
        }
      }
      if (attempts < MAX_ATTEMPTS) {
        window.setTimeout(() => { void tick(); }, RECONCILE_MS);
        return;
      }
      setPipelineStalled(true);
    };

    const timer = window.setTimeout(() => { void tick(); }, RECONCILE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, callId, reviewCall?.hasIntelligence, awaitingAutomaticPost, failed, dispatch]);

  return { reviewCall, pipelineStage, pipelineError, pipelineStalled, loadFailed, refresh };
};
